import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  dubRows,
  dubCount,
  dubTargets,
  dubEtaLabel,
  dubFileName,
  dubIndexCsv,
  zipStore,
  safeFileName,
  bookAudioKey,
  bookAudioLegacyKey,
  normalizeDubVoice,
  DUB_VOICES,
  DUB_PACE_MS,
  type DubLang,
  type DubRow,
  type DubTarget,
  type DubVoice,
  subtitleGaps,
  ghostAudioKeys,
  ghostAudioSay,
} from "@greg-chick/shared";
import {
  listBooks,
  loadBookDoc,
  loadManifest,
  publishAssets,
  publishBook,
  generateNarration,
  uploadAsset,
  readPageText,
  type BookDoc,
  type BookInfo,
  type ManifestEntry,
} from "../lib/contentApi";
import { useRegisterSave, discardDraft } from "../lib/saveBar";
import { loadRepoBookDoc } from "../lib/repoAssets";
import { ActionButton } from "./ActionButton";
import { Msg, Empty } from "./ui";
import { saveBlob, fetchBytes, csvBytes, bytesBlob } from "../lib/download";

/**
 * 🎙️ **읽어주기(더빙)** — 책 한 권의 소리를 한자리에서 만든다.
 *
 * ## 왜 새 화면인가
 * 만드는 버튼은 원래 [📖 전자책] 안, **쪽 편집기 깊숙이** 있었다. 쪽마다 하나씩. 34쪽짜리
 * 책을 한국어·영어로 다 읽히려면 **쪽을 열고 → 누르고 → 다음 쪽으로** 를 예순여덟 번 해야 했다.
 * 중간에 어디까지 했는지도 알 수 없다 — 쪽을 하나하나 열어 봐야 안다.
 *
 * 이건 「책 한 권을 고치는 일」이 아니라 **「책방 전체의 소리를 채우는 일」** 이라 화면을 나눴다.
 *
 * ## 없는 칸을 두 가지로 나눠 보여 준다
 * `bookDub.ts` 참고. 요약하면 — **눌러서 되는 칸(⬜)** 과 **눌러도 안 되는 칸(⚠️ 자막 없음)** 은
 * 다른 문제다. 뭉쳐 놓으면 운영자는 눌러 보고, 아무 일도 안 생기고, 왜인지 알 수 없다.
 *
 * ## 만든다고 아이에게 나가지 않는다
 * 만든 소리는 **초안**이다(docs/build/00 S4). 들어 보고 [🚀 웹에 올리기]를 눌러야 나간다.
 * 그래서 하단 저장바에 등록한다 — 이 화면만 저장 버튼을 따로 갖지 않는다.
 *
 * ## 중간에 멈춰도 된다
 * 엣지 TTS 는 분당 20개로 막혀 있어 70개면 4분쯤 걸린다. 그동안 탭을 닫으면 안 되는데,
 * 사람은 닫는다. 그래서 **이미 있는 것은 절대 다시 만들지 않는다** — 다시 눌러도 남은 것만 한다.
 */
export function BookDub({ onGoBook }: { onGoBook?: () => void }) {
  const [books, setBooks] = useState<BookInfo[]>([]);
  const [bookId, setBookId] = useState<string>("");
  const [doc, setDoc] = useState<BookDoc | null>(null);
  const [assets, setAssets] = useState<Record<string, ManifestEntry>>({});
  /** 이번에 만든 것 — 아직 웹에 안 올라간 초안. 저장바가 이걸 올린다. */
  const [pending, setPending] = useState<Record<string, ManifestEntry>>({});
  const [voice, setVoice] = useState<DubVoice>("nova");
  /** 책 문서가 바뀌었나 — 목소리 바꿈 + 영어 자막 초안. 저장바의 「바꾼 것 N개」에 들어간다. */
  const [docDirty, setDocDirty] = useState(false);
  const [langs, setLangs] = useState<Record<DubLang, boolean>>({ ko: true, en: true });
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /** 지금 만들고 있는 칸(`<쪽>.<말>`)과, 칸마다의 실패 사유. */
  const [now, setNow] = useState<string | null>(null);
  const [failed, setFailed] = useState<Record<string, string>>({});
  const [run, setRun] = useState<{ done: number; total: number } | null>(null);
  /** 「멈추기」 — 리액트 상태로 두면 돌고 있는 반복문이 옛 값을 본다. */
  const stop = useRef(false);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const [list, man] = await Promise.all([listBooks(), loadManifest()]);
        if (!alive) return;
        setBooks(list);
        setAssets(man.assets);
        if (list.length > 0) setBookId((cur) => cur || list[0]!.id);
      } catch (e) {
        if (alive) setErr((e as Error).message);
      } finally {
        if (alive) setBusy(false);
      }
    })();
    setBusy(true);
    return () => {
      alive = false;
    };
  }, []);

  // 책을 바꾸면 그 책의 본문·목소리를 읽는다. 만들던 초안은 남긴다(다른 책 것이라 겹치지 않는다).
  useEffect(() => {
    if (!bookId) return;
    let alive = true;
    void (async () => {
      try {
        const d = await loadBookDoc(bookId);
        if (!alive) return;
        setDoc(d);
        setPicked(new Set());
        setFailed({});
        // [불러오기]로 다른 책의 보관본을 얹는 중이면, 방금 읽은 것 위에 그걸 덮는다.
        const waiting = draftRef.current;
        if (waiting && waiting.bookId === bookId) {
          draftRef.current = null;
          applyDraft(waiting);
          return;
        }
        setVoice(normalizeDubVoice(d.narrationVoice));
        setDocDirty(false);
      } catch (e) {
        if (alive) setErr((e as Error).message);
      }
    })();
    return () => {
      alive = false;
    };
  }, [bookId]);

  /**
   * ✏️ **원본 글**(저장소 `book.<id>.json` — 스캔본을 옮겨 적은 글). 게시본 자막에 **빠진 말**이 있는지
   * 견주려고 읽는다(shared `subtitleGaps` — `pnpm book:seed` 와 같은 목록).
   *
   * 🔴 (2026-09-10) 《폴짝》 세 쪽 자막이 스캔본보다 짧았다 — 읽어주기도 그만큼 짧게 읽었다.
   *    저장소는 고쳤는데 게시본은 운영자 것이라 스크립트가 안 덮는다. 여기서 **한 번에** 채운다.
   * 못 읽으면 `null` — 카드를 안 띄울 뿐 「빠진 말 없음」이라고 하지 않는다(없다 ≠ 못 물어봤다).
   */
  const [repoPages, setRepoPages] = useState<BookDoc["pages"] | null>(null);
  const [gapFilled, setGapFilled] = useState<number | null>(null);
  useEffect(() => {
    setRepoPages(null);
    setGapFilled(null);
    if (!bookId) return;
    let alive = true;
    void loadRepoBookDoc(bookId).then((d) => alive && setRepoPages(d?.pages ?? null));
    return () => {
      alive = false;
    };
  }, [bookId]);
  const gaps = useMemo(() => (doc && repoPages ? subtitleGaps(doc.pages, repoPages) : []), [doc, repoPages]);

  /**
   * 👻 **없는 책의 소리** — 책은 지웠는데 소리만 게시본에 남은 것(2026-09-11 · 20개가 「114개」를 부풀리고 있었다).
   * 판정은 shared `ghostAudioKeys`. 🔴 책 목록이 비었으면 **안 센다** — 못 받은 것을 「전부 유령」으로 보이면 안 된다.
   * 지우기는 **숨김 표시**(`hidden: true`, 주소는 비운다) — 게시본에서 키를 빼면 정적 기본이 되살아난다(이 저장소의 규칙).
   */
  const ghosts = useMemo(() => (books.length > 0 ? ghostAudioKeys({ ...assets, ...pending }, books.map((b) => b.id)) : []), [assets, pending, books]);
  const [ghostsCleared, setGhostsCleared] = useState<number | null>(null);
  function clearGhosts() {
    if (ghosts.length === 0) return;
    setPending((pd) => {
      const n = { ...pd };
      for (const k of ghosts) n[k] = { url: "", hidden: true };
      return n;
    });
    setGhostsCleared(ghosts.length);
  }

  /**
   * ✏️ **원본 글로 채우기** — 자막을 채우고, 그 쪽에 이미 있는 소리를 「🔁 다시 만들 칸」으로 표시한다.
   *
   * 🔴 소리를 표시하지 않으면 자막만 길어지고 **소리는 옛 글 그대로** 남는다(「이미 있는 것은 다시
   *    안 만든다」). 그래서 이미 있는 소리에 **옛 글**(`fromText` = 방금 바꾼 게시본 글)을 적어 둔다 —
   *    그 소리가 실제로 그 글로 만들어졌으니 거짓이 아니고, 저장해도 표시가 남는다.
   * 🔴 쓰는 것은 **초안**이다. 아이에게는 아래 [💾 저장하기] 뒤에 간다.
   */
  function fillGaps() {
    if (!doc || !bookId || gaps.length === 0) return;
    const all = { ...assets, ...pending };
    setDoc({
      ...doc,
      pages: doc.pages.map((p) => {
        const mine = gaps.filter((g) => g.pageId === String((p as { id?: unknown }).id));
        if (mine.length === 0) return p;
        const next = { ...p };
        for (const g of mine) next[g.lang === "ko" ? "subtitle_ko" : "subtitle_en"] = g.want;
        return next;
      }),
    });
    setPending((pd) => {
      const n = { ...pd };
      for (const g of gaps) {
        const key = bookAudioKey(bookId, g.pageId, g.lang);
        const e = all[key];
        // 이미 적힌 것이 있으면 그걸로 이미 낡은 줄 안다 — 덮지 않는다
        if (e?.url && !e.fromText) n[key] = { ...e, fromText: g.have };
      }
      return n;
    });
    setDocDirty(true);
    setGapFilled(gaps.length);
  }

  /** 판정은 shared 한 곳 — 출시 준비 목록·`pnpm book:audio` 와 같은 규칙으로 센다. */
  const rows: DubRow[] = useMemo(() => {
    if (!doc || !bookId) return [];
    const all = { ...assets, ...pending };
    // 🔁 소리마다 **어떤 글로 만들었나**(fromText)를 넘긴다 — 자막과 다르면 낡은 소리다(shared `dubRows`)
    return dubRows(bookId, doc.pages as { id: string }[], (k) => !!all[k], (k) => all[k]?.fromText);
  }, [doc, bookId, assets, pending]);

  const koCount = useMemo(() => dubCount(rows, "ko"), [rows]);
  const enCount = useMemo(() => dubCount(rows, "en"), [rows]);
  const chosenLangs = useMemo(
    () => (["ko", "en"] as DubLang[]).filter((l) => langs[l]),
    [langs],
  );

  const missingTargets = useMemo(
    () => (bookId ? dubTargets(bookId, rows, chosenLangs) : []),
    [bookId, rows, chosenLangs],
  );
  const pickedTargets = useMemo(
    () => (bookId ? dubTargets(bookId, rows, chosenLangs, { only: [...picked] }) : []),
    [bookId, rows, chosenLangs, picked],
  );
  const redoTargets = useMemo(
    () => (bookId ? dubTargets(bookId, rows, chosenLangs, { redo: true }) : []),
    [bookId, rows, chosenLangs],
  );

  const dirtyCount = Object.keys(pending).length + (docDirty ? 1 : 0);

  /**
   * 💾 **하던 일 보관** — 저장바의 [💾 저장하기]가 이걸 이 기기에 적는다.
   *
   * 담는 것: 아직 안 올린 소리 목록(`pending`) · 목소리 · 책 문서(영어 자막 초안이 들어 있다).
   * mp3 자체는 이미 서버에 있으니 **주소만** 담긴다 — 브라우저 저장소를 채우지 않는다.
   *
   * 🔴 **어느 책 것인지 같이 적는다.** 안 적으면 다른 책을 보다가 [불러오기]를 눌렀을 때
   * 「폴짝」의 소리 목록이 「도서관」에 얹힌다 — 키가 책 이름을 품고 있어 화면에는 아무것도
   * 안 나타나고, 게시하면 엉뚱한 키가 늘어난다.
   */
  const draftRef = useRef<{ bookId: string; pending: Record<string, ManifestEntry>; voice: DubVoice; doc: BookDoc | null } | null>(null);

  useRegisterSave({
    id: "bookdub",
    label: "읽어주기",
    dirty: dirtyCount,
    draft: {
      data: () => ({ bookId, pending, voice, doc }),
      restore: (d) => {
        const r = d as { bookId?: string; pending?: Record<string, ManifestEntry>; voice?: unknown; doc?: BookDoc };
        if (!r?.bookId) return;
        if (r.bookId === bookId) {
          applyDraft(r);
        } else {
          // 다른 책 것이다 — 그 책을 먼저 연 뒤(아래 효과에서) 얹는다.
          draftRef.current = { bookId: r.bookId, pending: r.pending ?? {}, voice: normalizeDubVoice(r.voice), doc: r.doc ?? null };
          setBookId(r.bookId);
        }
      },
    },
    save: async () => {
      if (Object.keys(pending).length > 0) {
        // 올리기 직전 최신본 위에 얹는다 — 다른 탭에서 그림을 올렸어도 지우지 않게.
        const fresh = await loadManifest();
        await publishAssets({ ...fresh.assets, ...pending });
        setAssets({ ...fresh.assets, ...pending });
        setPending({});
      }
      // 목소리는 **책 문서**에 남는다. 안 남기면 다음에 한 쪽만 다시 만들 때 목소리가 달라진다.
      if (docDirty && doc && bookId) {
        const next = { ...doc, narrationVoice: voice };
        await publishBook(bookId, next);
        setDoc(next);
        setDocDirty(false);
      }
      // 웹에 올라갔으면 보관본은 할 일을 다했다 — 안 버리면 「저장해 둔 게 있어요」가 계속 뜨고,
      // 불러오면 이미 올린 것이 「안 올린 것」으로 되살아난다.
      discardDraft("bookdub");
    },
  });

  /** 보관본을 화면에 얹는다. 책을 여는 효과와 **같은 함수**를 쓰게 따로 뺐다. */
  function applyDraft(r: { pending?: Record<string, ManifestEntry>; voice?: unknown; doc?: BookDoc | null }) {
    if (r.doc) setDoc(r.doc);
    setVoice(normalizeDubVoice(r.voice));
    setPending(r.pending ?? {});
    setDocDirty(true);
  }

  /** 한 칸 만들기 — 실패해도 다음 칸으로 간다(70개 중 하나 때문에 전부 멈추지 않게). */
  const makeOne = useCallback(
    async (t: DubTarget): Promise<boolean> => {
      const cell = `${t.pageId}.${t.lang}`;
      setNow(cell);
      try {
        const blob = await generateNarration(t.text, voice);
        const file = new File([blob], `${t.key}.mp3`, { type: "audio/mpeg" });
        const url = await uploadAsset(t.key, file);
        setPending((p) => ({
          ...p,
          // 🔁 **어떤 글로 만들었나**를 같이 적는다 — 나중에 자막을 고치면 「낡은 소리」로 알아본다
          [t.key]: { url, alt: `AI 나레이션(${t.lang === "ko" ? "한국어" : "English"}·${voice})`, fromText: t.text },
        }));
        setFailed((f) => {
          if (!f[cell]) return f;
          const { [cell]: _drop, ...rest } = f;
          return rest;
        });
        return true;
      } catch (e) {
        setFailed((f) => ({ ...f, [cell]: (e as Error).message }));
        return false;
      } finally {
        setNow(null);
      }
    },
    [voice],
  );

  /**
   * 목록을 순서대로 돈다. **한 번에 하나씩**, 사이를 띄우고.
   *
   * 동시에 여러 개를 던지면 엣지가 429 로 막고(분당 20개), 그러면 진행 막대가 멈춘 것처럼
   * 보인다. 처음부터 천천히 가는 편이 전체로는 빠르고 남은 시간도 정확하다.
   */
  const runAll = useCallback(
    async (targets: DubTarget[]) => {
      if (targets.length === 0) return;
      stop.current = false;
      setRun({ done: 0, total: targets.length });
      for (let i = 0; i < targets.length; i++) {
        if (stop.current) break;
        await makeOne(targets[i]!);
        setRun({ done: i + 1, total: targets.length });
        if (i < targets.length - 1 && !stop.current) {
          await new Promise((r) => setTimeout(r, DUB_PACE_MS));
        }
      }
      setRun(null);
    },
    [makeOne],
  );

  /**
   * ⚠️ **영어 자막이 없어 막힌 쪽들을 한 번에.**
   *
   * 칸마다 버튼을 두는 것만으로는 부족하다 — 지금 그런 칸이 20개다(도서관 12쪽·경주 8쪽).
   * 스무 번 누르게 두면 이 화면을 만든 이유가 없어진다.
   *
   * 그림을 읽는 일이라 소리보다 느리고 비싸다. 그래서 **막힌 쪽만** 돈다.
   */
  /** ⚠️ 자막이 없어 못 만드는 칸 — 그림을 읽어 **영어 자막 초안**을 채운다(저장은 사람이). */
  const draftEnglish = useCallback(
    async (pageId: string) => {
      if (!bookId || !doc) return;
      const hint = typeof doc.title_ko === "string" ? doc.title_ko : undefined;
      const got = await readPageText(bookId, pageId, hint);
      if (!got.subtitle_en.trim()) throw new Error("그림에서 읽을 글자를 못 찾았어요.");
      // 한국어 자막은 **건드리지 않는다** — 운영자가 이미 고쳐 둔 글일 수 있다.
      setDoc((d) =>
        d
          ? {
              ...d,
              pages: d.pages.map((p) =>
                (p as { id?: string }).id === pageId ? { ...p, subtitle_en: got.subtitle_en } : p,
              ),
            }
          : d,
      );
      setDocDirty(true); // 책 문서가 바뀌었다 → 저장바가 올린다
    },
    [bookId, doc],
  );

  const blockedEn = useMemo(() => rows.filter((r) => !r.wordless && r.state.en === "no_text"), [rows]);

  const draftAllEnglish = useCallback(async () => {
    stop.current = false;
    setRun({ done: 0, total: blockedEn.length });
    const fails: string[] = [];
    for (let i = 0; i < blockedEn.length; i++) {
      if (stop.current) break;
      const r = blockedEn[i]!;
      setNow(`${r.pageId}.en`);
      try {
        await draftEnglish(r.pageId);
      } catch (e) {
        fails.push(`${r.pageId}: ${(e as Error).message}`);
      } finally {
        setNow(null);
      }
      setRun({ done: i + 1, total: blockedEn.length });
      if (i < blockedEn.length - 1 && !stop.current) {
        await new Promise((res) => setTimeout(res, DUB_PACE_MS));
      }
    }
    setRun(null);
    // 조용히 삼키지 않는다 — 몇 쪽이 안 됐는지는 말해 줘야 다시 시도할지 정할 수 있다.
    if (fails.length > 0) setErr(`영어 자막을 못 읽은 쪽 ${fails.length}개 — ${fails[0]}`);
  }, [blockedEn, draftEnglish]);

  /** 지금 화면이 보고 있는 그림표(게시본 + 이번에 만든 것). */
  const allAssets = useMemo(() => ({ ...assets, ...pending }), [assets, pending]);
  const urlOf = useCallback(
    (pageId: string, lang: DubLang): string | undefined =>
      // 자리 이름·폴백 규칙 모두 shared 한 곳 — 뷰어가 찾는 것과 같아야 한다
      // 🔴 `??` 는 비운 자리(`{ url: "" }`)를 값으로 쳐서 **옛 자리 소리를 가린다** —
      //    뷰어는 `firstOnWeb` 으로 고쳤다. 여기도 같은 규칙이어야 「관리자엔 없다는데
      //    아이 화면에선 소리가 난다」가 안 생긴다.
      allAssets[bookAudioKey(bookId, pageId, lang)]?.url ||
      allAssets[bookAudioLegacyKey(pageId, lang)]?.url,
    [allAssets, bookId],
  );

  const bookTitle = useMemo(
    () => safeFileName(books.find((b) => b.id === bookId)?.title ?? bookId, bookId || "책"),
    [books, bookId],
  );

  /** 💾 **한 개만** 내 컴퓨터로. 다른 출처라 그냥 링크로는 저장이 안 된다(lib/download 참고). */
  const saveOne = useCallback(
    async (row: DubRow, lang: DubLang) => {
      const url = urlOf(row.pageId, lang);
      if (!url) throw new Error("아직 만들지 않은 소리예요.");
      const bytes = await fetchBytes(url);
      saveBlob(bytesBlob(bytes, "audio/mpeg"), `${bookTitle}_${dubFileName(row.index, lang)}`);
    },
    [urlOf, bookTitle],
  );

  /** 지금 받을 수 있는 것(만들어진 것만). 「없는 파일까지 받는 척」 하지 않는다. */
  const savable = useMemo(
    () =>
      rows.flatMap((r) =>
        chosenLangs
          .filter((l) => r.state[l] === "done" && urlOf(r.pageId, l))
          .map((l) => ({ row: r, lang: l })),
      ),
    [rows, chosenLangs, urlOf],
  );

  /**
   * 💾 **한 번에 다** — ZIP 하나로 묶어 준다.
   *
   * 서른네 개를 한 개씩 받게 두면 브라우저가 「여러 파일 내려받기 허용?」을 묻고, 이름도
   * 제각각 겹친다. 묶음 안에는 **목록표(csv)** 도 같이 넣는다 — 파일만 서른네 개 받으면
   * 어느 소리가 어느 문장인지 알 수가 없다.
   *
   * 받다 실패한 것이 있으면 **몇 개인지 말한다.** 조용히 빠지면 「다 받았다」고 믿게 된다.
   */
  const saveAll = useCallback(async () => {
    if (savable.length === 0) throw new Error("아직 받을 소리가 없어요.");
    stop.current = false;
    setRun({ done: 0, total: savable.length });
    const files: { name: string; bytes: Uint8Array }[] = [];
    const fails: string[] = [];
    try {
      for (let i = 0; i < savable.length; i++) {
        if (stop.current) break;
        const { row, lang } = savable[i]!;
        const url = urlOf(row.pageId, lang)!;
        try {
          files.push({ name: `${bookTitle}/${dubFileName(row.index, lang)}`, bytes: await fetchBytes(url) });
        } catch (e) {
          fails.push(`${dubFileName(row.index, lang)}: ${(e as Error).message}`);
        }
        setRun({ done: i + 1, total: savable.length });
      }
    } finally {
      setRun(null);
    }
    if (files.length === 0) throw new Error(`하나도 받지 못했어요 — ${fails[0] ?? "알 수 없는 이유"}`);
    files.push({ name: `${bookTitle}/목록.csv`, bytes: csvBytes(dubIndexCsv(rows, chosenLangs)) });
    const zip = zipStore(files);
    saveBlob(bytesBlob(zip, "application/zip"), `${bookTitle}_읽어주기.zip`);
    if (fails.length > 0) setErr(`${fails.length}개는 못 받았어요 — ${fails[0]}`);
  }, [savable, urlOf, bookTitle, rows, chosenLangs]);

  const toggle = (pageId: string) =>
    setPicked((s) => {
      const n = new Set(s);
      if (n.has(pageId)) n.delete(pageId);
      else n.add(pageId);
      return n;
    });

  const pickMissing = () =>
    setPicked(new Set(rows.filter((r) => chosenLangs.some((l) => r.state[l] === "missing")).map((r) => r.pageId)));

  if (busy) return <p className="hint">불러오는 중…</p>;
  if (books.length === 0) {
    return (
      <Empty action={onGoBook ? <button className="btn" onClick={onGoBook}>📖 전자책으로</button> : undefined}>
        아직 책이 없어요. 먼저 전자책을 만들어 주세요.
      </Empty>
    );
  }

  return (
    <section className="dub">
      <Msg text={err} kind="err" />

      <div className="dub__head">
        <label className="dub__pick">
          <span>책</span>
          <select value={bookId} onChange={(e) => setBookId(e.target.value)} disabled={!!run}>
            {books.map((b) => (
              <option key={b.id} value={b.id}>
                {b.title} ({b.pages}쪽)
              </option>
            ))}
          </select>
        </label>

        <label className="dub__pick">
          <span>🎙️ 이 책의 목소리</span>
          <select
            value={voice}
            disabled={!!run}
            onChange={(e) => {
              setVoice(normalizeDubVoice(e.target.value));
              setDocDirty(true);
            }}
          >
            {DUB_VOICES.map((v) => (
              <option key={v.id} value={v.id}>
                {v.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      {/* 🔴 한 책에 두 목소리가 섞이면 아이가 8쪽에서 목소리가 바뀌는 걸 듣는다. 파일·화면엔 이상이 없다. */}
      <p className="hint">
        한 권은 처음부터 끝까지 한 목소리여야 해요. 이미 만든 소리가 있는데 목소리를 바꾸면,
        <b> 새로 만드는 쪽만 </b>목소리가 달라집니다.
      </p>

      {ghosts.length > 0 ? (
        <div className="dub__gaps" role="region" aria-label="없는 책의 소리">
          <p>
            👻 <b>없는 책의 소리 {ghosts.length}개</b> — 책은 지웠는데 소리만 남았어요({ghostAudioSay(ghosts)}).
            아이 화면엔 안 나오지만 「소리 몇 개」를 셀 때마다 부풀려 보여요.
          </p>
          <button type="button" className="btn btn--sm" disabled={!!run} onClick={clearGhosts}>
            🧹 정리하기
          </button>
        </div>
      ) : null}
      {ghostsCleared ? (
        <Msg kind="ok" text={`🧹 ${ghostsCleared}개를 정리했어요. 아래 [💾 저장하기]를 눌러야 게시본에서 빠져요.`} />
      ) : null}
      {gaps.length > 0 ? (
        <div className="dub__gaps" role="region" aria-label="자막에 빠진 말">
          <p>
            ✏️ <b>자막에 빠진 말이 있는 칸 {gaps.length}곳</b> — 원본(스캔본을 옮겨 적은 글)보다 짧아요.
            읽어주기도 <b>그만큼 빼먹고</b> 읽어요.
          </p>
          <ul>
            {gaps.map((g) => (
              <li key={`${g.pageId}.${g.lang}`}>
                {g.no}쪽 {g.lang === "ko" ? "한국어" : "English"} — 지금 「{g.have}」 → 원본 「{g.want}」
              </li>
            ))}
          </ul>
          <button type="button" className="btn btn--sm" disabled={!!run} onClick={fillGaps}>
            원본 글로 채우기
          </button>
        </div>
      ) : null}
      {gapFilled ? (
        <Msg
          kind="ok"
          text={`✏️ ${gapFilled}곳을 채웠어요. 그 쪽 소리는 「🔁 다시 만들 칸」으로 표시했어요 — [✨ 빠진 것·바뀐 것 전부 만들기]로 다시 만든 뒤 아래 [💾 저장하기]를 눌러야 아이에게 가요.`}
        />
      ) : null}

      <div className="dub__bars">
        <Bar label="한국어" c={koCount} />
        <Bar label="English" c={enCount} />
      </div>

      <div className="dub__langs">
        {(["ko", "en"] as DubLang[]).map((l) => (
          <label key={l} className="dub__lang">
            <input
              type="checkbox"
              checked={langs[l]}
              disabled={!!run}
              onChange={() => setLangs((s) => ({ ...s, [l]: !s[l] }))}
            />
            {l === "ko" ? "한국어" : "English"}
          </label>
        ))}
      </div>

      <div className="dub__actions">
        {run ? (
          <>
            <div className="dub__progress" role="status" aria-live="polite">
              <div className="dub__progress-fill" style={{ width: `${(run.done / run.total) * 100}%` }} />
              <span>
                {run.done} / {run.total} 만드는 중… 이 탭을 열어 두세요
              </span>
            </div>
            <button type="button" className="btn" onClick={() => (stop.current = true)}>
              ■ 멈추기
            </button>
          </>
        ) : (
          <>
            <ActionButton
              className="btn btn--primary"
              disabled={missingTargets.length === 0 || chosenLangs.length === 0}
              onAction={() => runAll(missingTargets)}
              doneLabel="다 만들었어요 — 들어 보고 [🚀 웹에 올리기]"
            >
              {/* 🔁 자막이 바뀐 칸도 여기서 함께 만든다(shared `dubTargets`) — 이름에서 그걸 말해 준다 */}
              {koCount.stale + enCount.stale > 0 ? "✨ 빠진 것·바뀐 것 전부 만들기" : "✨ 빠진 것만 전부 만들기"}
              {missingTargets.length > 0
                ? ` · ${missingTargets.length}개 · ${dubEtaLabel(missingTargets.length)}`
                : ""}
            </ActionButton>
            <ActionButton
              className="btn"
              disabled={pickedTargets.length === 0}
              onAction={() => runAll(pickedTargets)}
              doneLabel="만들었어요"
            >
              고른 것만 만들기{pickedTargets.length > 0 ? ` · ${pickedTargets.length}개` : ""}
            </ActionButton>
            <button type="button" className="btn btn--sm" onClick={pickMissing}>
              빠진 쪽 모두 고르기
            </button>
            {blockedEn.length > 0 ? (
              <ActionButton
                className="btn"
                onAction={draftAllEnglish}
                doneLabel="채웠어요 — 읽어 보고 고친 뒤 [🚀 웹에 올리기]"
              >
                🤖 영어 자막 없는 {blockedEn.length}쪽, 그림 읽어 초안 만들기
              </ActionButton>
            ) : null}
            <ActionButton
              className="btn"
              disabled={savable.length === 0}
              onAction={saveAll}
              doneLabel="내 컴퓨터에 저장했어요"
              title="만들어진 소리를 ZIP 하나로 묶어 받아요(목록표 포함)"
            >
              💾 전부 내려받기{savable.length > 0 ? ` · ${savable.length}개` : ""}
            </ActionButton>
            <ActionButton
              className="btn btn--sm"
              disabled={redoTargets.length === 0}
              confirm={`이미 만든 것까지 ${redoTargets.length}개를 전부 다시 만들어요. 계속할까요?`}
              onAction={() => runAll(redoTargets)}
              doneLabel="다시 만들었어요"
            >
              ↻ 전부 다시 만들기
            </ActionButton>
          </>
        )}
      </div>

      <table className="dub__table">
        <thead>
          <tr>
            <th aria-label="고르기" />
            <th>쪽</th>
            <th>자막</th>
            <th>한국어</th>
            <th>English</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.pageId} data-wordless={r.wordless ? "true" : undefined}>
              <td>
                <input
                  type="checkbox"
                  checked={picked.has(r.pageId)}
                  disabled={!!run || r.wordless}
                  onChange={() => toggle(r.pageId)}
                  aria-label={`${r.index === 0 ? "표지" : `${r.index}쪽`} 고르기`}
                />
              </td>
              <td className="dub__no">{r.index === 0 ? "표지" : `${r.index}쪽`}</td>
              <td className="dub__text">{r.text.ko || <i>(자막 없음)</i>}</td>
              {(["ko", "en"] as DubLang[]).map((l) => (
                <Cell
                  key={l}
                  row={r}
                  lang={l}
                  url={{ ...assets, ...pending }[bookAudioKey(bookId, r.pageId, l)]?.url}
                  running={now === `${r.pageId}.${l}`}
                  error={failed[`${r.pageId}.${l}`]}
                  locked={!!run}
                  onMake={() =>
                    runAll(
                      dubTargets(bookId, [r], [l], { redo: r.state[l] === "done" }),
                    )
                  }
                  onDraftEn={() => draftEnglish(r.pageId)}
                  onSave={() => saveOne(r, l)}
                />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/** 「몇 개 중 몇 개」 — 자막이 없어 못 만드는 칸은 분모에서 빼고 따로 적는다. */
function Bar({ label, c }: { label: string; c: { done: number; missing: number; stale: number; blocked: number } }) {
  const total = c.done + c.missing;
  const pct = total === 0 ? 0 : (c.done / total) * 100;
  return (
    <div className="dub__bar">
      <span className="dub__bar-label">{label}</span>
      <div className="dub__bar-track">
        <div className="dub__bar-fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="dub__bar-num">
        {c.done} / {total}
        {c.stale > 0 ? <b className="dub__blocked"> · 🔁 자막이 바뀌어 다시 만들 칸 {c.stale}</b> : null}
        {c.blocked > 0 ? <b className="dub__blocked"> · 자막 없어 못 만드는 칸 {c.blocked}</b> : null}
      </span>
    </div>
  );
}

/** 한 칸. 상태 다섯 가지가 **서로 다르게 보여야** 한다 — 뭉치면 눌러도 안 되는 이유를 알 수 없다. */
function Cell({
  row,
  lang,
  url,
  running,
  error,
  locked,
  onMake,
  onDraftEn,
  onSave,
}: {
  row: DubRow;
  lang: DubLang;
  url?: string;
  running: boolean;
  error?: string;
  locked: boolean;
  onMake: () => Promise<void>;
  onDraftEn: () => Promise<void>;
  /** 💾 이 한 개만 내 컴퓨터로. 만들어진 칸에서만 보인다. */
  onSave: () => Promise<void>;
}) {
  const state = row.state[lang];
  if (row.wordless) return <td className="dub__cell dub__cell--skip">— 건너뜀</td>;

  if (running) {
    return (
      <td className="dub__cell dub__cell--busy">
        <span aria-live="polite">⏳ 만드는 중…</span>
      </td>
    );
  }

  if (state === "no_text") {
    return (
      <td className="dub__cell dub__cell--blocked">
        <span title="읽을 자막이 없어요. 소리를 만들 수 없어요.">⚠️ 자막 없음</span>
        {lang === "en" ? (
          <ActionButton className="btn btn--sm" disabled={locked} onAction={onDraftEn} doneLabel="채웠어요">
            🤖 영어 자막 초안
          </ActionButton>
        ) : null}
      </td>
    );
  }

  return (
    <td className={`dub__cell dub__cell--${state}`}>
      {state === "stale" ? (
        // 🔁 소리는 있는데 **지금 자막과 다른 글**로 만든 것 — 들어 볼 수는 있게 두고, 다시 만들라고 한다
        <span title="자막이 바뀌었어요. 이 소리는 옛 글을 읽어요.">🔁 자막이 바뀌었어요</span>
      ) : null}
      {(state === "done" || state === "stale") && url ? (
        <>
          <Play url={url} />
          <ActionButton className="btn btn--sm" onAction={onSave} doneLabel="저장했어요" title="이 소리 하나만 내 컴퓨터로">
            💾
          </ActionButton>
        </>
      ) : (
        <span>⬜ 없음</span>
      )}
      <ActionButton
        className="btn btn--sm"
        disabled={locked}
        onAction={onMake}
        doneLabel={state === "missing" ? "만들었어요" : "다시 만들었어요"}
      >
        {state === "done" ? "↻" : state === "stale" ? "다시 만들기" : "만들기"}
      </ActionButton>
      {error ? <span className="dub__err">🔴 {error}</span> : null}
    </td>
  );
}

/** ▶️ 들어보기 — 한 번에 하나만 난다(여러 개가 겹쳐 나면 뭘 듣는지 알 수 없다). */
let current: HTMLAudioElement | null = null;
function Play({ url }: { url: string }) {
  const [on, setOn] = useState(false);
  useEffect(() => () => {
    if (current) {
      current.pause();
      current = null;
    }
  }, []);
  return (
    <button
      type="button"
      className="btn btn--sm"
      aria-pressed={on}
      onClick={() => {
        if (current) {
          current.pause();
          current = null;
        }
        if (on) {
          setOn(false);
          return;
        }
        const a = new Audio(url);
        current = a;
        a.onended = () => setOn(false);
        void a.play().then(
          () => setOn(true),
          () => setOn(false),
        );
      }}
    >
      {on ? "■" : "▶️"} 듣기
    </button>
  );
}
